<template>
  <SettingsLayout class="bg-white dark:bg-slate-900">
    <template #body>
      <div class="settings-content bg-white dark:bg-slate-900 px-12">
        <!-- 筛选区域 -->
        <div class="filter-section">
          <!-- 筛选条件区域 -->
          <div class="flex flex-col w-full space-y-4">
            <div class="flex flex-col items-center w-full md:flex-row">
              <!-- 时间范围 -->
              <div class="md:w-[320px] w-full multiselect-wrap--small">
                <label class="block text-xs font-medium mb-2">
                  {{ $t('BLACKLIST.FILTERS.DATE_RANGE') }}
                </label>
                <WootDateRangePicker
                  show-range
                  :value="isDefaultDateRange ? null : dateRange"
                  :confirm-text="$t('REPORT.CUSTOM_DATE_RANGE.CONFIRM')"
                  :placeholder="$t('BLACKLIST.FILTERS.START_TIME_TO_END_TIME')"
                  @change="onDateRangeChange"
                />
              </div>
              
              <!-- 访客ID -->
              <div class="mx-2 md:w-[320px] w-full multiselect-wrap--small">
                <label class="block text-xs font-medium mb-2">
                  {{ $t('BLACKLIST.FILTERS.VISITOR_ID') }}
                </label>
                <Input
                  v-model="filters.visitorId"
                  :placeholder="$t('BLACKLIST.FILTERS.VISITOR_ID_PLACEHOLDER')"
                />
              </div>

              <!-- 客服账号 -->
              <div class="mx-1 md:w-[320px] w-full multiselect-wrap--small">
                <label class="block text-xs font-medium mb-2">
                  {{ $t('BLACKLIST.FILTERS.AGENT_ID') }}
                </label>
                <div class="multiselect-wrap--small">
                  <multiselect
                    v-model="filters.agentId"
                    :options="agents"
                    track-by="id"
                    label="name"
                    selected-label=""
                    deselect-label=""
                    :select-label="''"
                    :searchable="false"
                    :allow-empty="false"
                    :placeholder="$t('BLACKLIST.FILTERS.AGENT_ID_PLACEHOLDER')"
                  >
                    <template #option="{ option }">
                      <div class="flex items-center gap-2">
                        <Thumbnail
                          :src="option.thumbnail"
                          :status="option.availability_status"
                          :username="option.name"
                          size="22px"
                        />
                        <p class="my-0 text-slate-800 dark:text-slate-75">
                          {{ option.name }}
                        </p>
                      </div>
                    </template>
                    <template #singleLabel="{ option }">
                      <div class="flex items-center gap-2">
                        <Thumbnail
                          :src="option.thumbnail"
                          :status="option.availability_status"
                          :username="option.name"
                          size="22px"
                        />
                        <span class="blacklist-option__desc">
                          <span class="my-0 text-slate-800 dark:text-slate-75">{{
                            option.name
                          }}</span>
                        </span>
                      </div>
                    </template>
                  </multiselect>
                </div>
              </div>

              <!-- 模糊搜索 -->
              <div class="mx-1 md:w-[320px] w-full multiselect-wrap--small">
                <label class="block text-xs font-medium mb-2">
                  {{ $t('BLACKLIST.FILTERS.SEARCH') }}
                </label>
                <Input
                  v-model="filters.query"
                  :placeholder="$t('BLACKLIST.FILTERS.SEARCH_PLACEHOLDER')"
                />
              </div>
            </div>

            <!-- 操作按钮区域 - 单独一行 -->
            <div class="flex items-center justify-start w-full mt-4">
              <WootButton 
                variant="primary"
                class="mr-2"
                @click="onSearch">
                {{ $t('BLACKLIST.FILTERS.SEARCH_BUTTON') }}
              </WootButton>
              <WootButton 
                variant="clear"
                @click="onReset">
                {{ $t('BLACKLIST.FILTERS.RESET_BUTTON') }}
              </WootButton>
            </div>
          </div>
        </div>

        <!-- 列表区域 -->
        <div class="list-section">
          <BlacklistTable 
            :blacklist="blacklist"
            :isLoading="uiFlags.isFetching"
            :pageIndex="pageIndex"
            :totalCount="meta.totalCount || 0"
            @page-change="onPageNumberChange"
            @delete-blacklist="onDeleteBlacklist"
          />
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>

<script setup>
import { ref, computed, onMounted, reactive } from 'vue';
import { useStore } from 'vuex';
import SettingsLayout from '../SettingsLayout.vue';
import BlacklistTable from './components/BlacklistTable.vue';
import Input from 'dashboard/components/widgets/forms/Input.vue';
import WootDateRangePicker from 'dashboard/components/ui/DateRangePicker.vue';
import WootButton from 'dashboard/components/ui/WootButton.vue';
import endOfDay from 'date-fns/endOfDay';
import startOfDay from 'date-fns/startOfDay';
import Thumbnail from 'dashboard/components/widgets/Thumbnail.vue'
import { format } from 'date-fns';
import { convertLocalToUTC } from './components/BlacklistUtils';

const store = useStore();

// --- Vuex State and Getters ---
const blacklist = computed(() => store.getters['blacklist/getBlacklist']);
const uiFlags = computed(() => store.getters['blacklist/getUIFlags']);
const meta = computed(() => store.getters['blacklist/getMeta']);

// 分页索引，从 0 开始计数
const pageIndex = ref(0);

// 筛选条件
const filters = reactive({
  visitorId: '',
  agentId: null,
  query: '',
  // 可以添加其他筛选条件
});

// 日期范围
const dateRange = ref([new Date(), new Date()]);
const isDefaultDateRange = ref(true);

// 客服列表状态
const agents = ref([]);

// 获取客服列表的方法
const fetchAgents = async () => {
  try {
    await store.dispatch('agents/get'); // 假设你的 store 中有 agents 模块
    agents.value = store.getters['agents/getAgents'];
  } catch (error) {
    // 处理错误
  }
};

// --- 方法 ---
const fetchBlacklistRecords = () => {
  // 构建查询参数
  const params = {};
  
  // 添加访客ID作为email参数
  if (filters.visitorId) {
    params.email = filters.visitorId;
  }
  
  // 添加客服账号作为agent_id参数
  if (filters.agentId) {
    params.usersId = filters.agentId.id;
  }
  
  // 添加模糊搜索参数
  if (filters.query) {
    params.query = filters.query;
  }
  
  // 添加日期范围参数
  if (!isDefaultDateRange.value) {
    
    // 处理开始日期 - 当天的 00:00:00
    const startDate = startOfDay(dateRange.value[0]);
    
    // 处理结束日期 - 当天的 23:59:59
    const endDate = new Date(endOfDay(dateRange.value[1]));
    // 确保结束时间是 23:59:59，而不是 23:59:59.999
    endDate.setMilliseconds(0);
  
    // 将日期转换为 ISO 8601 UTC 格式
    params.startDate = convertLocalToUTC(startDate);
    params.endDate = convertLocalToUTC(endDate);
  }
  
  // 添加分页参数
  params.page = pageIndex.value + 1; // API可能从1开始计数
  params.size = 30;
  
  // 调用API获取数据
  store.dispatch('blacklist/fetchBlacklistRecords', params);
};

const onPageNumberChange = (page) => {
  pageIndex.value = page;
  fetchBlacklistRecords();
};

// 处理删除黑名单事件
const onDeleteBlacklist = () => {
  // 删除成功后刷新列表
  fetchBlacklistRecords();
};

// 搜索按钮点击事件
const onSearch = () => {
  pageIndex.value = 0; // 重置为第一页
  fetchBlacklistRecords();
};

// 重置按钮点击事件
const onReset = () => {
  // 重置所有筛选条件
  filters.visitorId = '';
  filters.agentId = null;
  filters.query = '';
  
  pageIndex.value = 0; // 重置为第一页
  dateRange.value = [new Date(), new Date()];
  isDefaultDateRange.value = true;
  fetchBlacklistRecords();
};

// 日期范围改变事件
const onDateRangeChange = (value) => {
  dateRange.value = value;
  isDefaultDateRange.value = false;
};

// 确保国际化资源加载完成
const ensureI18nLoaded = async () => {
  // 通常不需要特殊处理，如果有需要可以在这里添加逻辑
  // 例如可以检查特定的国际化键是否已加载
  return true;
};

// 组件挂载时处理
onMounted(async () => {
  // 先确保国际化资源加载完成
  await ensureI18nLoaded();
  // 获取客服列表
  await fetchAgents();
  // 获取黑名单列表
  fetchBlacklistRecords();
});
</script>

<style lang="scss" scoped>
.filter-section {
  margin-bottom: 1.5rem;
}

.list-section {
  margin-top: 1.5rem;
}

// 使用新的 Vue 3 深度选择器语法
:deep(.date-picker) {
  .mx-datepicker {
    width: 100%;
  }
}

.settings-content {
  min-height: calc(100vh - 4.5rem);
  width: 100%;
}

/* multiselect 样式调整 */
:deep(.multiselect) {
  min-height: 42px;
  
  .multiselect__tags {
    min-height: 42px;
    padding: 0 40px 0 8px;
    border: 1px solid #d1d5db;
    border-radius: 0.375rem;
  }

  .multiselect__placeholder {
    white-space: nowrap;
    color: #94a3b8; /* 更浅的灰色 */
    font-size: 0.875rem; /* 14px */
    padding-top:10px;
  }
}

/* 按钮样式 */
:deep(.search-button) {
  background-color: #3b82f6; /* 蓝色背景 */
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 0.375rem;
  
  &:hover {
    background-color: #2563eb; /* 悬停时深蓝色 */
  }
}

:deep(.reset-button) {
  border: 1px solid #d1d5db; /* 浅色边框 */
  color: #3b82f6; /* 蓝色文字 */
  padding: 0.5rem 1.5rem;
  border-radius: 0.375rem;
  
  &:hover {
    border-color: #3b82f6; /* 悬停时边框变蓝 */
    background-color: #eff6ff; /* 悬停时浅蓝色背景 */
  }
}

/* 日期选择按钮样式 */
button {
  transition: all 0.2s;
}
</style>